/**
 * Handing a person to the device's own phone and mail apps.
 *
 * A link, not an integration, for the same reason as the directions handoff:
 * the dialler and the mail client are already signed in, already hold the
 * user's signature and call history, and already work with the car's
 * Bluetooth. Nothing we could build in the app would be better than getting
 * out of the way.
 *
 * Both builders return `null` rather than a best-effort string when the detail
 * is missing or unusable. A dead `tel:` link on a phone does not fail visibly:
 * it opens the dialler with nothing in it, or does nothing at all, and the user
 * cannot tell which. Returning null lets the caller hide the action instead,
 * which is honest about what it does not know.
 */

/**
 * The fewest digits that can be a real, dialable number.
 *
 * Short codes and internal extensions are shorter than this, but they are not
 * what a partner's contact card carries, and treating a stray "1" or a year as
 * a phone number produces an action that silently fails.
 */
const MIN_DIALABLE_DIGITS = 7

/** Trailing extension, in the forms people actually type into a CRM. */
const EXTENSION = /(?:\s*(?:x|ext\.?|extension)\s*)(\d{1,6})\s*$/i

/**
 * `tel:` URL for a phone number, or null if it cannot be dialled.
 *
 * Strips the formatting people type (spaces, dots, brackets, hyphens) because
 * `tel:` wants the dialable string, and keeps a leading `+` because dropping it
 * turns an international number into a wrong one.
 *
 * An extension is preserved as the RFC 3966 `;ext=` parameter rather than
 * pasted into the number. Dialling "5551234x89" as a single string reaches
 * nobody.
 */
export const telHref = (phone?: string | null): string | null => {
	if (!phone || typeof phone !== 'string') return null

	const trimmed = phone.trim()
	if (!trimmed) return null

	const extensionMatch = trimmed.match(EXTENSION)
	const extension = extensionMatch?.[1]
	const main = extension ? trimmed.slice(0, extensionMatch.index) : trimmed

	// A leading + is meaningful; a + anywhere else is noise.
	const international = main.trimStart().startsWith('+')
	const digits = main.replace(/\D/g, '')

	if (digits.length < MIN_DIALABLE_DIGITS) return null

	const number = international ? `+${digits}` : digits
	return extension ? `tel:${number};ext=${extension}` : `tel:${number}`
}

/**
 * Anything with a single @, something before it, and a dotted domain after.
 *
 * Deliberately loose. This is deciding whether to OFFER a mail link, not
 * whether an address is deliverable, and a stricter pattern would hide the
 * button on addresses that work.
 */
const EMAIL = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/

/**
 * `mailto:` URL for an address, or null if there is nothing usable.
 *
 * No subject or body: this is "write to this person", and a pre-filled subject
 * would be our guess at their business showing up in their sent items.
 */
export const mailtoHref = (email?: string | null): string | null => {
	if (!email || typeof email !== 'string') return null

	const trimmed = email.trim()
	if (!EMAIL.test(trimmed)) return null

	// Encode the address itself: a + in a tagged address is legal and must not
	// be read as a space by the mail client.
	return `mailto:${encodeURIComponent(trimmed)}`
}
